import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import AuthProvider from '@/features/auth/context/auth-provider'
import { ThemeProvider } from '@/shared/components/theme-provider'
import { handleMutationError, isRetryableError } from '@/shared/utils/handle-api-error'
import '@/assets/global.css'
import * as Sentry from '@sentry/react'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { HelmetProvider } from 'react-helmet-async'
import { RouterProvider } from 'react-router'
import router from '@/app/router/router-config'
import { TailwindIndicator } from '@/shared/components/tailwind-indicator'
import { UpdateBanner } from '@/shared/components/update-banner'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (!isRetryableError(error)) return false
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000)
    },
    mutations: {
      onError: (error) => {
        handleMutationError(error)
      }
    }
  }
})

function SentryFallback() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
      <h1 className="text-2xl font-bold text-destructive">Error inesperado</h1>
      <p className="text-muted-foreground">
        Ocurrió un error inesperado. Por favor, recarga la página.
      </p>
      <button
        onClick={() => {
          window.location.reload()
        }}
        type="button"
        className="px-4 py-2 text-sm font-medium border rounded-md hover:bg-accent"
      >
        Recargar página
      </button>
    </div>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <UpdateBanner />
        <Toaster position="top-right" richColors closeButton expand={false} />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <NuqsAdapter>
              <HelmetProvider>
                {/* react-router v7 eliminó fallbackElement (el fallback de
                    hidratación se define a nivel de ruta con HydrateFallback) */}
                <RouterProvider router={router} />
              </HelmetProvider>
            </NuqsAdapter>
          </AuthProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          <TailwindIndicator />
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
